import clsx from "clsx";
import { Minus } from "lucide-react";
import { useEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";

import { useGetProject } from "~/api/projects/projects";
import { Checkbox } from "~/components/ui/checkbox";
import { Table, TableHead, TableHeader, TableRow } from "~/components/ui/table";
import { useProjectParams } from "~/helpers";
import { useMobile } from "~/hooks/use-mobile";

import { Document } from "~/api/model";
import { getSelectableDocuments } from "../../helpers";
import {
  isBatesEligible,
  useIsBatesEnabled,
} from "../../hooks/useBatesActions";
import { useDocumentSelectionStore } from "../../stores/documentSelectionStore";

export interface DocumentsListHeaderProps {
  documents: Document[];
  allFilesNotProcessed: boolean;
}

export const DocumentsListHeader = ({
  documents,
  allFilesNotProcessed,
}: DocumentsListHeaderProps) => {
  const { organizationId, caseId } = useProjectParams();
  const { data: caseData } = useGetProject(organizationId, caseId);
  const { getSelectedCount, toggleAllDocuments } = useDocumentSelectionStore();
  const { isMobile } = useMobile();

  const isBatesEnabled = useIsBatesEnabled();
  const batesPendingCount = isBatesEnabled
    ? documents.filter((doc) => isBatesEligible(doc)).length
    : 0;
  const selectedCount = getSelectedCount();
  const selectableDocuments = getSelectableDocuments(
    documents || [],
    allFilesNotProcessed,
    caseData
  );
  const hasSelectableDocuments = selectableDocuments.length > 0;
  const totalSelectableCount = selectableDocuments.length;

  const checkboxRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (checkboxRef.current) {
      const inputElement = checkboxRef.current.querySelector(
        'input[type="checkbox"]'
      );
      if (inputElement) {
        (inputElement as HTMLInputElement).indeterminate =
          selectedCount > 0 && selectedCount < totalSelectableCount;
      }
    }
  }, [selectedCount, totalSelectableCount]);

  // Hide table header on mobile - documents will show as cards
  if (isMobile) {
    return null;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="h-10 text-muted-foreground text-zinc-500 text-sm font-medium font-inter leading-tight grid grid-cols-12 gap-4 px-2">
          <TableHead
            className={`${isBatesEnabled ? "col-span-6" : "col-span-7"} flex items-center`}
          >
            <Checkbox
              ref={checkboxRef}
              checked={
                selectedCount > 0 && selectedCount === totalSelectableCount
              }
              onCheckedChange={() =>
                toggleAllDocuments(
                  documents || [],
                  allFilesNotProcessed,
                  caseData
                )
              }
              aria-label="Select all documents"
              className={clsx(
                "mr-4 cursor-pointer data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 border border-[#E4E4E7] shadow-none",
                !hasSelectableDocuments && "opacity-50 cursor-not-allowed"
              )}
              uncheckedIcon={
                selectedCount > 0 && selectedCount < totalSelectableCount ? (
                  <Minus className="h-4 w-4 text-white" />
                ) : null
              }
              disabled={!hasSelectableDocuments}
            />
            <FormattedMessage id="documentsList.name" />
          </TableHead>
          {isBatesEnabled && (
            <TableHead className="col-span-1 flex items-center gap-1">
              <FormattedMessage
                id="documentsList.bates"
                defaultMessage="Ref #"
              />
              {batesPendingCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 text-[10px] font-semibold">
                  {batesPendingCount}
                </span>
              )}
            </TableHead>
          )}
          <TableHead className="col-span-1 flex items-center">
            <FormattedMessage
              id="documentsList.status"
              defaultMessage={"Status"}
            />
          </TableHead>
          <TableHead className="col-span-1 flex items-center">
            <FormattedMessage
              id="documentsList.pages"
              defaultMessage={"Pages"}
            />
          </TableHead>
          <TableHead className="col-span-1 flex items-center">
            <FormattedMessage id="documentsList.size" />
          </TableHead>
          <TableHead className="text-right col-span-2" />
        </TableRow>
      </TableHeader>
    </Table>
  );
};
